/* eslint-disable no-restricted-globals */
/* eslint-disable import/extensions */
/* eslint-disable radix */
/* eslint-disable no-param-reassign */

/**
 * Class for capturing game progression, player characterization, and system environment data for all DDNA events
 */
class DDNADataCapture {
    /**
     * constructor
     */
    constructor() {
        this._sessionId = DDNA.utils.generateUniqueId();
        this._sessionData = null;
        this._systemParamObj = null;
        this._pcData = null;
        this._jsInitTime = Date.now();
        this._loadingTime = null;

        /*
         *  COOK-OMT:
         *  Primary ID = game backend ID if connected to game backend, FBIG ID if not
         *  Secondary ID = FBIG ID
         *
         *  Other OMT:
         *  Primary ID = FBIG ID
         *  Secondary ID = game backend ID if connected to game backend, '-1' if not
         *
         *  The primary ID is sent as the userId in all events,
         *  the secondary IDs is sent as a separate parameter in all events
         */

        this._isCOOK = G.BuildEnvironment.APP_ID.toUpperCase() === 'COOKIE-CRUSH-2';

        if (this._isCOOK) {
            this._primaryId = OMT.envData.gbUserId || OMT.envData.settings.user.userId;
            this._secondaryId = OMT.envData.settings.user.userId;
        } else {
            this._primaryId = OMT.envData.settings.user.userId;
            this._secondaryId = OMT.envData.gbUserId || '-1';
        }

        this.createDefaultPlayerCharacterizationSessionData();
    }

    /**
     * set the javascript init time
     * @param {number} jsInitTime
     */
    setJavaScriptInitTime(jsInitTime) {
        this._jsInitTime = jsInitTime;
    }

    /**
     * assign generic user data. userID, sessionID, etc
     * @param {Object} eventObj event object instance
     */
    assignUserData(eventObj) {
        eventObj.userID = this._primaryId;
        eventObj.sessionID = this._sessionId;
    }

    /**
     * create the environment paramObj to be included with all events
     */
    createEnvParamObj() {
        const date = new Date();

        // system / environment variables.
        this._systemParamObj = {
            userIDHash: OMT.crypt.sha3Hash(this._primaryId),
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            // eslint-disable-next-line prefer-destructuring
            timezoneName: /\((.*)\)/.exec(date.toString())[1],
            timezoneOffset: DDNA.utils.getTimezoneOffsetString(date),
            operatingSystem: DDNA.utils.getOperatingSystem(),
            gameName: OMT.envData.settings.game.slug,
            gameID: OMT.envData.settings.game.id,
            gameVersion: G.BuildEnvironment.version ? G.BuildEnvironment.version : '0.0',
            locale: OMT.envData.settings.env.locale,
            durationSinceJSEnabled: 0, // *** OMT-COOK Duplicate
            timeSinceJSEnabled: 0,
            loadingTime: 0,
            platform: OMT.envData.platform,
            isCookOMT: OMT.envData.settings.game.isCookOMT ? 1 : 0,
            browserName: OMT.systemInfo.browserName,
            browserVersion: OMT.systemInfo.browserVersion,
            iapTier: OMT.envData.settings.env.tier,

            // TODO: track FPS properly, (rolling average)
            fps: game.time.fps,
        };

        if (this._isCOOK) {
            this._systemParamObj.facebookUserID = this._secondaryId;
            this._systemParamObj.facebookUserIDHash = OMT.crypt.sha3Hash(this._secondaryId);
        } else {
            this._systemParamObj.gbUserID = this._secondaryId;
            this._systemParamObj.gbUserIDHash = OMT.crypt.sha3Hash(this._secondaryId);
        }
    }

    /**
     * assign system / environment parameters to the paramObj.
     * @param {Object} paramObj event paramObj instance
     */
    assignEnvParams(paramObj) {
        if (!this._systemParamObj) this.createEnvParamObj();

        this._systemParamObj.durationSinceJSEnabled = Date.now() - this._jsInitTime;
        this._systemParamObj.timeSinceJSEnabled = this._systemParamObj.durationSinceJSEnabled; // *** COOK-OMT Duplicate
        paramObj.fps = game.time.fps;

        if (!this._loadingTime) this._loadingTime = this._systemParamObj.durationSinceJSEnabled;
        this._systemParamObj.loadingTime = this._loadingTime;

        Object.assign(paramObj, this._systemParamObj);
    }

    /**
     * assign game progression parameters to the paramObj.
     * @param {Object} paramObj event paramObj instance
     */
    assignGameProgressionParams(paramObj) {
        if (!G.saveState || !G.saveState.data) return; // save data is not yet available

        // inventory values
        paramObj.lives = parseInt(G.saveState.getLives());
        paramObj.coins = parseInt(G.saveState.getCoins());
        paramObj.swapBooster = parseInt(G.saveState.getBoosterAmount(1));
        paramObj.removerBooster = parseInt(G.saveState.getBoosterAmount(2));
        paramObj.horizontalBooster = parseInt(G.saveState.getBoosterAmount(3));
        paramObj.verticalBooster = parseInt(G.saveState.getBoosterAmount(4));
        paramObj.extraMovesBooster = parseInt(G.saveState.getBoosterAmount(6));
        paramObj.specialTileBooster = parseInt(G.saveState.getBoosterAmount(7));
        paramObj.bombBooster = parseInt(G.saveState.getBoosterAmount(8));
        paramObj.unlimitedLivesMinutes = Math.ceil(G.saveState.getUnlimitedLivesSec() / 60);

        // progressions values
        paramObj.levelsToNextGate = G.Helpers.gatesMgr.getLevelsToFirstClosedGate();
        paramObj.starsToNextGate = G.Helpers.gatesMgr.getStarsNeededForNextClosedGate();
        paramObj.highestLevel = G.saveState.getLastPassedLevelNr() + 1;
        paramObj.highestLevelID = G.json.levels.order[paramObj.highestLevel - 1];
        paramObj.dailyMissionCandy = this._getDailyMissionCandy();
        paramObj.treasureHuntGems = this._getTreasureHuntGems();
        paramObj.stars = G.saveState.getAllStars();

        const winStreak = G.saveState.mysteryGift_getCurrentStreak();
        const winStreakGifts = G.saveState.mysteryGift_getCurrentGifts();
        paramObj.winStreakProgress = G.saveState.mysteryGift_isModeReady() ? winStreak : -1;
        paramObj.winStreakBombBooster = winStreakGifts.indexOf('spiral') >= 0 ? 1 : 0;
        paramObj.winStreakVertical = winStreakGifts.indexOf('vertical') >= 0 ? 1 : 0;
        paramObj.winStreakHorizontal = winStreakGifts.indexOf('horizontal') >= 0 ? 1 : 0;
        paramObj.winStreakCross = winStreakGifts.indexOf('cross') >= 0 ? 1 : 0;

        paramObj.consecutiveLoginDays = G.saveState.getLoginStats().consecutiveLoginDays;
        paramObj.dailyMissionProgress = G.dailyMissionsMgr.getProgressPercent();
        paramObj.treasureHuntProgress = G.TreasureHunt.treasureHuntMgr.getProgressPercent();

        paramObj.consecutiveWinCount = G.saveState.getCWinCount();
        paramObj.consecutiveWinHighest = G.saveState.getHighestCWinCount();
        paramObj.timerForGate = this._getTimerForGate();
        paramObj.timerForSpin = G.saveState.getSecUntilNextFreeSpin();
        paramObj.timerForNextLife = G.saveState.getSecUntilNextLifeRefill();
        paramObj.timerForGift = G.Helpers.dailyRewardMgr.getSecToNextDailyGift();

        paramObj.visitedFanPage = G.saveState.data.visitedFanPage || false;
    }

    /**
     * create an object with the initial values for player characterization parameters
     * @returns {object}
     */
    createDefaultPlayerCharacterizationParamObj() {
        return {
            realMoneySpent: 0,
            coinValueBoughtWithRealMoney: 0,
            realMoneyPurchasesMade: 0,
            coinsSpent: 0,
            coinPurchasesMade: 0,
            zeroLivesCoinPurchases: 0,
            zeroLivesRealMoneyPurchases: 0,
            daysFromFirstLogin: 0,
            daysInGame: 1,
            sessionNumber: 1,
            lastLevelAttempted: -1,
            lastLevelWon: -1,
            adsWatchedRewarded: 0,
            adsWatchedInterstitial: 0,
            adsLastTimeRewarded: -1,
            adsLastTimeShown: -1,
            numFriendsInApp: 0,
            // post card
            seenPostcardShareBtn: 0,
            usedPostcardShareBtn: 0,
            // friendship chest
            seenFriendshipChestPromo: 0,
            seenFriendshipChestShareBtn: 0,
            usedFriendshipChestShareBtn: 0,
            // daily reward
            seenDailyRewardShareBtn: 0,
            usedDailyRewardShareBtn: 0,
            // hard level
            seenHardLevelShareBtn: 0,
            usedHardLevelShareBtn: 0,
            // achievement gift
            seenAchievementGiftShareBtn: 0,
            usedAchievementGiftShareBtn: 0,
            // mission complete
            seenMissionCompleteShareBtn: 0,
            usedMissionCompleteShareBtn: 0,
            // treasure hunt
            seenTreasureHuntShareBtn: 0,
            usedTreasureHuntShareBtn: 0,
            // ad fallback
            seenAdFallbackInviteBtn: 0,
            usedAdFallbackInviteBtn: 0,
            // gate unlock
            seenGateUnlockInviteBtn: 0,
            usedGateUnlockInviteBtn: 0,
        };
    }

    /**
     * set data referemce for player charecterization data
     * @param {Object} pcData player charecterization data object
     */
    async setPlayerCharacterizationData(pcData) {
        const paramObj = Object.assign(this.createDefaultPlayerCharacterizationParamObj(), pcData.params);
        console.log("datacapture");
        paramObj.numFriendsInApp = (await OMT.friends.getFriendsList()).length; // update num friends
        this._pcData = pcData;
        this._pcData.params = paramObj;
    }

    /**
     * save the player charecterization data to the server
     */
    writePlayerCharacterizationData() {
        DDNA.tracking.addToSaveData('pcData', this._pcData, true);
    }

    /**
     * create and set up new paramObj with the initial values for player characterization parameters
     */
    async createDefaultPlayerCharacterizationData() {
        const paramObj = this.createDefaultPlayerCharacterizationParamObj();
        try {
            paramObj.botMessageActive = G.saveState.getBotOptIn() ? 1 : 0;
        } catch (e) {
            paramObj.botMessageActive = 0;
        }

        // data object for tracking pc param value changes
        const todaysDate = new Date();
        const trackingData = {};
        trackingData.lastDate = todaysDate.getTime();

        await this.setPlayerCharacterizationData({ params: paramObj, trackingData });
        this.updatePlayerCharacterizationLoginParams(false);
        this.writePlayerCharacterizationData();
    }

    /**
     * create a session paramObj with the initial values for player characterization parameters
     */
    createDefaultPlayerCharacterizationSessionData() {
        this._sessionData = {
            params: {
                levelsPassedThisSession: 0,
                levelAttemptsThisSession: 0,
                inGameBoostersUsedThisSession: 0,
                mapBoostersUsedThisSession: 0,
                extraMovesRescueBoughtThisSession: 0,
                adsWatchedThisSessionRewarded: 0,
                adsWatchedThisSessionInterstitial: 0,
                coinsSpentThisSession: 0,
                coinsEarnedThisSession: 0,
                lossAversionActionsThisSession: 0,
                realMoneySpentThisSession: 0,
            },
        };
    }

    /**
     * update parameters related to login statistics
     * @param {boolean} save write data (optional)
     */
    updatePlayerCharacterizationLoginParams(save = true) {
        const paramObj = this._pcData.params;
        const { trackingData } = this._pcData;
        const todaysDate = new Date();
        const lastRecordedDate = new Date(trackingData.lastDate);

        // increment day count
        if (!DDNA.utils.isSameDate(lastRecordedDate, todaysDate)) {
            paramObj.daysInGame++;
        }

        // days since first login date
        if (G.saveState && G.saveState.data) {
            const loginStats = G.saveState.getLoginStats();
            const creationDate = new Date(loginStats.creation);
            paramObj.daysFromFirstLogin = DDNA.utils.getDateDayDifference(creationDate, todaysDate);
        }

        // store todays date
        trackingData.lastDate = todaysDate.getTime();

        // increment session number
        paramObj.sessionNumber = paramObj.sessionNumber ? paramObj.sessionNumber + 1 : 1;

        if (save) this.writePlayerCharacterizationData();
    }

    /**
     * set a player characterization parameter value
     * @param {string} key key / parameter name to assign
     * @param {*} value value to assign
     * @param {boolean} save write data (optional)
     */
    setPlayerCharacterizationParam(key, value, save = true) {
        if (this._pcData.params[key] === value) return; // value did not change
        this._pcData.params[key] = value;
        if (save) this.writePlayerCharacterizationData();
    }

    /**
     * get a player characterization parameter value
     * @param {string} key
     */
    getPlayerCharacterizationParam(key) {
        return this._pcData.params[key];
    }

    /**
     * add to a player characterization parameter value
     * @param {string} key key / parameter name to assign
     * @param {*} value value to assign
     * @param {boolean} save write data (optional)
     */
    addToPlayerCharacterizationParam(key, value, save = true) {
        this._pcData.params[key] += value;
        if (key === 'realMoneySpent') this._pcData.params[key] = Math.floor(this._pcData.params[key] * 100) / 100; // round to 2 digits
        if (save) this.writePlayerCharacterizationData();
    }

    /**
     * assign player characterization parameters to the paramObj.
     * @param {Object} paramObj paramObject to assign values to
     */
    assignPlayerCharacterizationParams(paramObj) {
        if (!this._pcData || !this._pcData.params) return;
        Object.assign(paramObj, this._pcData.params);

        // Special case for timeSinceZeroLives
        delete paramObj.lastTimeAtZeroLives;
        paramObj.timeSinceZeroLives = this._pcData.params.lastTimeAtZeroLives > -1 ?
            Math.floor((Date.now() - this._pcData.params.lastTimeAtZeroLives) / 1000) :
            -1;
    }

    /**
     * set a session player characterization parameter value
     * @param {string} key key / parameter name to assign
     * @param {*} value value to assign
     * @param {boolean} save write data (optional)
     */
    setPlayerCharacterizationSessionParam(key, value) {
        if (!DDNA.tracking.enabled || this._sessionData.params[key] === value) return; // value did not change
        this._sessionData.params[key] = value;
    }

    /**
     * get a player characterization parameter value
     * @param {string} key
     */
    getPlayerCharacterizationSessionParam(key) {
        return this._sessionData.params[key];
    }

    /**
     * add to a player characterization parameter value
     * @param {string} key key / parameter name to assign
     * @param {*} value value to assign
     * @param {boolean} save write data (optional)
     */
    addToPlayerCharacterizationSessionParam(key, value) {
        if (!DDNA.tracking.enabled) return;
        // force 'coinsEarnedThisSession' there were some concatination errors on DDNA
        if (key === 'coinsEarnedThisSession') {
            if (isNaN(this._sessionData.params[key])) this._sessionData.params[key] = 0;
            this._sessionData.params[key] = parseInt(this._sessionData.params[key]) + parseInt(value);
        } else {
            this._sessionData.params[key] += value;
        }
        if (key === 'realMoneySpentThisSession') this._sessionData.params[key] = Math.floor(this._sessionData.params[key] * 100) / 100; // round to 2 digits
    }

    /**
     * assign player characterization session parameters to the paramObj.
     * @param {Object} paramObj paramObject to assign values to
     */
    assignPlayerCharacterizationSessionParams(paramObj) {
        if (!this._sessionData || !this._sessionData.params) return;
        Object.assign(paramObj, this._sessionData.params);
    }

    // Helper functions
    // TODO: move these to a helper class(es) later, maybe

    /**
     * get number of daily mission candies collected
     * @returns {number}
     */
    _getDailyMissionCandy() {
        const { goal } = G.dailyMissionsMgr.getProgress();
        return goal === 0 ? 0 : G.dailyMissionsMgr.getProgress().finished;
    }

    /**
     * get number of treasure hunt gems collected
     * @returns {number}
     */
    _getTreasureHuntGems() {
        const eventInstance = G.TreasureHunt.treasureHuntMgr.getProgressData();
        return eventInstance ? eventInstance.gemsCollected : 0;
    }

    /**
     * get timer for first unopened gate
     * @returns {number}
     */
    _getTimerForGate() {
        const firstUnopenedGateId = G.Helpers.gatesMgr.getUnopenedGatesIds()[0];
        if (firstUnopenedGateId === undefined) return -1;

        const gateObj = G.Helpers.gatesMgr.getGateObject(firstUnopenedGateId);
        return Math.max(-1, gateObj.getTimeToOpeningInSeconds());
    }
}

// create global references
window.DDNADataCapture = DDNADataCapture;